// @ts-check
// =============================================================================
// js/screens/duel/board.js — the 4×4 board, rendered in PIXI.
//
// WHAT THIS IS
//   The battlefield (old FlexBoardTile grid :3660/:4326, now PIXI containers
//   instead of DOM). Rows 0–1 are the opponent's side, rows 2–3 yours (the
//   server orients the board per viewer). Each occupied cell is a PixiTile
//   from the preserved generator; empty own-side cells are clickable "- ▽ -"
//   placeholders during your turn.
//
// PSEUDO-REACT RENDERING (three tiers, cheapest wins)
//   renderBoard()          entry point. FULL build only on first mount /
//                          layout change; otherwise per-slot RECONCILE.
//   reconcile (per slot)   each cell carries the KEY it was rendered from
//                          (tileKey: everything that affects the pixels).
//                          Same key -> the mounted tile survives untouched;
//                          different key -> only THAT cell re-renders.
//   applyBoardTargeting()  FEEDBACK pass — selection changes just restyle
//                          persistent rings. Never rebuilds anything.
//
// TARGETING LANGUAGE (shared with hand.js/bars.js — keep consistent)
//   selected = green ring · targeted = yellow ring w/ gentle pulse
//   BOARD: health at ZERO = the slump pose + DARK art; can't attack
//   (`ready` false) = a lighter dim; HAND grayscale = "can't act on this
//   right now" (grayFor). Leader portraits NEVER darken.
// =============================================================================

import * as PIXI from 'pixi.js';
import gsap from 'gsap';
import { gen, resolveCardArt } from '../../render/card-tile.js';
import { click, highlightFor } from './targeting.js';
import { applyHighlight } from './highlight-fx.js';
import { screenShake } from './animations.js';
import { BevelFilter } from 'pixi-filters';

/** Native size of a generator tile container = the Board_*.png texture
 *  (683×515 — all tile content sits inside it). This is the cell aspect too:
 *  the old client's 171×129 tiles ≈ 683:515. */
const TILE_NATIVE_W = 683;
const TILE_NATIVE_H = 515;

/** Highlight colors (identical hexes to the old client's CSS classes).
 *  'pickable' is no longer drawn — kept for the click-gating callers. */
export const HIGHLIGHT_COLORS = {
	pickable: 0xbae6ff,
	selected: 0xbaffcc,
	targeted: 0xfffcba,
};

/** The shared "can't act on this" look, split by layer: ARTWORK grays 50%
 *  and darkens a touch (the "resting" read), the FRAME/TEXT/GUI grays harder
 *  (75%) so the inert state reads clearly while the art stays recognizable.
 *  One instance of each, shared by every grayed tile/card (portraits are
 *  pure art -> the 50% one).
 *  resolution 'inherit': a filter rasterizes its subject into an intermediate
 *  texture at 1x by default, which is visibly BLURRY on high-DPI screens
 *  (the app renders at devicePixelRatio) — inherit matches the render target. */
export const GRAYSCALE_FILTER = new PIXI.ColorMatrixFilter();
export const GRAYSCALE_GUI_FILTER = new PIXI.ColorMatrixFilter();
GRAYSCALE_FILTER.saturate(-0.5, false);
GRAYSCALE_FILTER.brightness(0.78, true); // darker + grayer, still crisp
GRAYSCALE_GUI_FILTER.saturate(-0.75, false);

/** Board art shades, two levels: a creature that merely CAN'T ATTACK dims
 *  lightly; one at ZERO health goes properly dark (it's also slumped). */
const UNREADY_ART_FILTER = new PIXI.ColorMatrixFilter();
UNREADY_ART_FILTER.saturate(-0.35, false);
UNREADY_ART_FILTER.brightness(0.85, true);
const ZERO_ART_FILTER = new PIXI.ColorMatrixFilter();
ZERO_ART_FILTER.saturate(-0.5, false);
ZERO_ART_FILTER.brightness(0.55, true);

for (const filter of [GRAYSCALE_FILTER, GRAYSCALE_GUI_FILTER, UNREADY_ART_FILTER, ZERO_ART_FILTER]) {
	filter.resolution = 'inherit';
	filter.antialias = 'inherit';
}

/**
 * Gray a generator container (a PixiCard or PixiTile) in split layers: its
 * ART sprite (always child 0) at 50%, everything else (frame, texts, icons)
 * at 75%. On first use the non-art children are folded into one __guiLayer
 * sub-container (same coordinate space — pure regrouping) so the gui grays
 * in ONE filter pass instead of one per text object.
 * @param {any} el     The generator container (cell.wrap's tile / hand cardEl).
 * @param {boolean} gray
 * @returns {void}
 */
export function applyCardGrayscale(el, gray) {
	applyCardFilters(el, gray ? GRAYSCALE_FILTER : null, gray ? GRAYSCALE_GUI_FILTER : null);
}

/**
 * The underlying split-layer filter plumbing: art (child 0) and the folded
 * __guiLayer get independent filters (either may be null = untouched).
 * @param {any} el
 * @param {PIXI.ColorMatrixFilter|null} artFilter
 * @param {PIXI.ColorMatrixFilter|null} guiFilter
 * @returns {void}
 */
export function applyCardFilters(el, artFilter, guiFilter) {
	if (!el || el.destroyed) return;
	if (!el.__guiLayer) {
		const gui = new PIXI.Container();
		while (el.children.length > 1) {
			gui.addChild(el.children[1]);
		}
		el.addChild(gui);
		el.__guiLayer = gui;
		el.__artSprite = el.children[0];
	}
	const art = el.__artSprite;
	// Any pre-existing art filter is preserved under ours (board tiles have
	// none — the generator's dead-dim is stripped at mount).
	const base = art.__baseFilters ?? (art.__baseFilters = art.filters ? [...art.filters] : []);
	art.filters = artFilter ? [...base, artFilter] : (base.length ? [...base] : null);
	el.__guiLayer.filters = guiFilter ? [guiFilter] : null;
}

/**
 * The target attention cue, shared by board rings / hand plates / face pills:
 * the highlight stays SOLID and dips softly once every 10 seconds (a rapid
 * strobe was, in the words of live testing, "literally violent").
 * @param {any} obj  A display object (alpha is tweened).
 * @returns {void}
 */
export function gentlePulse(obj) {
	killPulse(obj);
	obj.__pulse = gsap.timeline({ repeat: -1, repeatDelay: 10 })
		.to(obj, { alpha: 0.45, duration: 0.45, ease: 'sine.inOut' })
		.to(obj, { alpha: 1, duration: 0.45, ease: 'sine.inOut' });
}

/**
 * Stop a gentle pulse (leaves alpha wherever the caller sets it next).
 * @param {any} obj
 * @returns {void}
 */
export function killPulse(obj) {
	if (obj.__pulse) {
		obj.__pulse.kill();
		obj.__pulse = null;
	}
}

/**
 * Everything that affects a tile's pixels, folded into a string. Equal key
 * means "the mounted tile is already correct — do not touch it".
 * @param {any} data  The board cell's card (resolveCardArt already run).
 * @returns {string}
 */
function tileKey(data) {
	const trueForm = data.trueForm === true ? 'hidden' : data.trueForm ? data.trueForm.waitCost : '';
	return [
		data.id, data.name, data.selectedArt || '', data.attack, data.health,
		(data.cropGain || 0) + (data.meatGain || 0), data.wait ?? '', trueForm,
	].join('|');
}

/**
 * Entry point: build or reconcile the board for a new duel state.
 * @param {object} scene    The duel scene (layers, metrics, zoom helpers).
 * @param {any} duel        The current duel snapshot (store.duel).
 * @returns {Promise<void>}
 */
export async function renderBoard(scene, duel) {
	if (!scene.boardCells) {
		// FULL build (first mount / relayout): every tile "appears" at once —
		// those are repaints, not arrivals, so big-entrance effects stay off
		// until the pass completes (see reconcileCell's boardPainted guard).
		scene.boardPainted = false;
		await mountBoard(scene, duel);
		scene.boardPainted = true;
		return;
	}
	// RECONCILE: touch only the slots whose content actually changed.
	const jobs = [];
	for (let y = 0; y < 4; y++) {
		for (let x = 0; x < 4; x++) {
			jobs.push(reconcileCell(scene, duel, scene.boardCells[y][x], y, x));
		}
	}
	applyBoardTargeting(scene);
	await Promise.all(jobs);
	scene.boardPainted = true;
}

/**
 * FULL build — first mount or after a layout change (duel.js clears
 * scene.boardCells to force this).
 * @param {object} scene
 * @param {any} duel
 */
async function mountBoard(scene, duel) {
	const layer = scene.layers.board;
	const overlay = scene.layers.boardOverlay;
	layer.removeChildren();
	overlay.removeChildren();
	scene.tiles = [[null, null, null, null], [null, null, null, null], [null, null, null, null], [null, null, null, null]];
	scene.boardCells = [];

	const { tileW, tileH, boardX, boardY, gap } = scene.metrics;
	const jobs = [];
	for (let y = 0; y < 4; y++) {
		scene.boardCells[y] = [];
		for (let x = 0; x < 4; x++) {
			const cellX = boardX + x * (tileW + gap);
			const cellY = boardY + y * (tileH + gap);

			// Static backing plate (also the click surface for empty cells),
			// drawn as a recessed SOCKET so the board reads as grooved slots
			// the tiles drop into: a darker floor + a REAL soft inset shadow
			// from an inverted BevelFilter (light from the bottom-right, so
			// the shadow hugs the top rim). The filter would normally cost a
			// GPU pass per plate per frame — cacheAsTexture bakes the result
			// once at mount (per-layout), so steady-state cost is a plain
			// textured quad. Relayouts rebuild the plates, re-baking cleanly.
			const plate = new PIXI.Graphics();
			drawCellSocket(plate, tileW, tileH, Math.min(12, tileW * 0.06));
			plate.filters = [new BevelFilter({
				rotation: 225,
				thickness: 5,
				lightColor: 0xffffff,
				lightAlpha: 0.07,
				shadowColor: 0x000000,
				shadowAlpha: 0.18,
			})];
			plate.cacheAsTexture({ antialias: true, resolution: scene.app?.renderer?.resolution || 2 });
			plate.x = cellX;
			plate.y = cellY;
			layer.addChild(plate);

			// Persistent highlight ring, in the overlay layer so it draws ABOVE
			// the tile art. Restyled in place by applyBoardTargeting.
			const ring = new PIXI.Graphics();
			ring.x = cellX;
			ring.y = cellY;
			overlay.addChild(ring);

			/** The mounted state of this slot. `data` stays current so the
			 *  zoom/click handlers (which read cell.data) never go stale. */
			const cell = {
				spot: `Board_${y}_${x}`, y, x, cellX, cellY, w: tileW, h: tileH,
				key: null, data: null, wrap: null, label: null, buildId: 0,
				plate, ring,
			};
			scene.boardCells[y][x] = cell;
			wireCellInput(scene, plate, cell);
			jobs.push(reconcileCell(scene, duel, cell, y, x));
		}
	}
	applyBoardTargeting(scene);
	await Promise.all(jobs);
}

/**
 * Draw one cell's SOCKET floor: just the recessed fill, a touch darker than
 * the battlefield panel (0x3a3149). The inset lighting is applied where the
 * plate is created (mountBoard): an inverted BevelFilter baked to a cached
 * texture — see the comment there for the light direction and cost story.
 * A mounted tile covers the socket entirely.
 * @param {PIXI.Graphics} g   The plate graphics (drawn at 0,0).
 * @param {number} w          Cell width.
 * @param {number} h          Cell height.
 * @param {number} r          Corner radius.
 */
function drawCellSocket(g, w, h, r) {
	g.roundRect(0, 0, w, h, r).fill(0x282334);
}

/**
 * Bring ONE slot in line with the new state. Same key -> no-op (the mounted
 * tile survives). Different key -> replace just this cell's content.
 * @param {object} scene
 * @param {any} duel
 * @param {any} cell
 * @param {number} y
 * @param {number} x
 */
async function reconcileCell(scene, duel, cell, y, x) {
	const data = duel.board[y]?.[x] || null;
	if (data) resolveCardArt(data, true);
	const newKey = data ? tileKey(data) : null;

	// The ▽ drop-target label wants to exist only on empty own-side cells
	// during your turn (state-dependent, so reconciled every update).
	const wantLabel = !data && y >= 2 && !!duel.player1.tick && !duel.spectating
		&& typeof duel.player1.hand !== 'number';

	// Keep handlers fresh even when the pixels didn't change (zoom reads it).
	const prevData = cell.data;
	cell.data = data;

	if (newKey === cell.key) {
		syncLabel(scene, cell, wantLabel);
		return;
	}
	cell.key = newKey;
	cell.buildId++; // abandons any in-flight tile build for this slot
	const buildId = cell.buildId;

	// Out with the old content… An EGG leaving a slot (hatched into its true
	// form, or shattered) cracks apart along a jagged line; the replacement
	// tile mounts underneath the separating halves. An egg that merely
	// PROGRESSED (its wait counter ticked — still an egg) re-renders quietly:
	// the countdown is part of the tile key, so it lands here every turn.
	const wasEgg = !!(prevData && prevData.trueForm);
	const stillEgg = !!(data && data.trueForm);
	if (cell.wrap) {
		if (wasEgg && !stillEgg) {
			spawnEggSplit(scene, cell);
		}
		// A slump tween mid-flight must not write to a destroyed tile.
		if (cell.wrap.children[0]) gsap.killTweensOf(cell.wrap.children[0]);
		cell.wrap.destroy({ children: true });
		cell.wrap = null;
		scene.tiles[y][x] = null;
	}
	syncLabel(scene, cell, wantLabel);
	if (!data) return;

	// …in with the new tile (async art — the plate stays clickable meanwhile).
	// HIDDEN INFO: an enemy egg's hatch goal must not show. In bot games the
	// server sends full info (no spectator/opponent stripping), so censor the
	// DISPLAY copy here: trueForm=true renders "(wait)" instead of
	// "(wait/waitCost)". Our own eggs keep the full countdown.
	// When WE run the Throne Mimic (hero 11), our own egg's real hatch goal is
	// 2 turns higher than the card's base waitCost (see engine CheckEggHatch),
	// so show wait/(waitCost+2). Owner-only, so nothing new leaks to opponents.
	let displayData = data;
	if (data.trueForm && data.trueForm !== true) {
		if (data.owner !== duel.player1.id) {
			displayData = { ...data, trueForm: true };
		} else if (duel.player1.hero == 11) {
			displayData = { ...data, trueForm: { ...data.trueForm, waitCost: data.trueForm.waitCost + 2 } };
		}
	}
	try {
		const tile = await gen.PixiTile(displayData, 0, 0, 'JustRender');
		if (cell.buildId !== buildId || !scene.boardCells) {
			tile.destroy({ children: true });
			return; // a newer state replaced this slot mid-flight
		}
		// Strip the generator's baked-in dead-creature dim (brightness 0.5 on
		// the art when health == 0 — it rasterizes at 1x and reads BLURRY).
		// Zeroed creatures keep full-color art; the slump pose carries the
		// state instead (styleBoardCell).
		if (tile.children[0]?.filters) {
			tile.children[0].filters = null;
		}
		const wrap = new PIXI.Container();
		wrap.addChild(tile);
		const scale = Math.min(cell.w / TILE_NATIVE_W, cell.h / TILE_NATIVE_H);
		tile.scale.set(scale);
		// Center-pivoted (same rendered placement as the old top-left math):
		// the spent-creature slump rotates around the tile's middle.
		tile.pivot.set(TILE_NATIVE_W / 2, TILE_NATIVE_H / 2);
		tile.x = cell.w / 2;
		tile.y = cell.h / 2;
		wrap.x = cell.cellX;
		wrap.y = cell.cellY;
		wireCellInput(scene, wrap, cell);
		scene.layers.board.addChild(wrap);
		scene.tiles[y][x] = wrap;
		cell.wrap = wrap;
		styleBoardCell(cell); // apply current dim/highlight state
		// BIG ENTRANCE: a creature ARRIVING with 10+ health rattles the screen
		// — a direct play, or an egg hatching into something huge. Eggs
		// themselves never qualify (they arrive as fragile shells; their
		// drama comes at the hatch). boardPainted keeps first paints and
		// relayout rebuilds silent — those are repaints, not arrivals.
		if (scene.boardPainted && data.creature && !data.trueForm
			&& data.health >= 10 && (!prevData || prevData.trueForm)) {
			screenShake(data.health);
		}
	} catch (error) {
		// One broken tile must never take out the board; the plate stays live.
		console.error(`PixiTile failed at ${cell.spot}:`, error);
	}
}

/**
 * The egg-crack effect: snapshot the leaving egg tile, cut it in two along a
 * RANDOM jagged vertical line, and pull the halves apart while they fall and
 * fade. Whatever replaces the egg (its hatched true form, or nothing when it
 * was shattered) renders underneath the separating halves.
 * @param {object} scene
 * @param {any} cell  The board cell whose wrap still holds the egg tile.
 */
function spawnEggSplit(scene, cell) {
	try {
		const renderer = scene.app?.renderer;
		if (!renderer || !cell.wrap) return;
		const texture = renderer.generateTexture(cell.wrap);
		const w = cell.w;
		const h = cell.h;

		// A jagged split line: top to bottom, wobbling around the middle.
		const jags = [];
		const steps = 5;
		for (let i = 0; i <= steps; i++) {
			jags.push({
				x: w / 2 + (i === 0 || i === steps ? (Math.random() - 0.5) * w * 0.1 : (Math.random() - 0.5) * w * 0.3),
				y: (h * i) / steps,
			});
		}

		const halves = [];
		for (const side of ['left', 'right']) {
			const holder = new PIXI.Container();
			holder.x = cell.cellX + w / 2;
			holder.y = cell.cellY + h / 2;
			const sprite = new PIXI.Sprite(texture);
			sprite.x = -w / 2;
			sprite.y = -h / 2;
			const mask = new PIXI.Graphics();
			const edgeX = side === 'left' ? -w / 2 - 4 : w / 2 + 4;
			mask.moveTo(edgeX, -h / 2 - 4);
			mask.lineTo(jags[0].x - w / 2, -h / 2 - 4);
			for (const jag of jags) {
				mask.lineTo(jag.x - w / 2, jag.y - h / 2);
			}
			mask.lineTo(edgeX, h / 2 + 4);
			mask.closePath();
			mask.fill(0xffffff);
			holder.addChild(sprite, mask);
			sprite.mask = mask;
			// BOTTOM of the fx layer: whatever caused the crack may still be
			// flying/lingering up there (e.g. the Chomp card parked on this
			// very egg) — the shell halves must separate UNDER it, not pop in
			// front and make the card frame seem to jump forward.
			scene.layers.fx.addChildAt(holder, 0);
			halves.push({ holder, dir: side === 'left' ? -1 : 1 });
		}

		for (const { holder, dir } of halves) {
			gsap.timeline()
				.to(holder, {
					x: holder.x + dir * w * 0.28,
					y: holder.y + h * 0.16,
					rotation: dir * 0.22,
					duration: 0.45,
					ease: 'power2.in',
				}, 0)
				.to(holder, { alpha: 0, duration: 0.2, ease: 'none' }, 0.28)
				.call(() => {
					if (!holder.destroyed) holder.destroy({ children: true });
					// The snapshot texture is shared by both halves — free it
					// once, after the second half finishes.
					if (!texture.__freed) texture.__freed = true;
					else texture.destroy(true);
				});
		}
	} catch (error) {
		// The crack is a garnish — a capture/mask failure must never block play.
		console.error('spawnEggSplit failed:', error);
	}
}

/**
 * Create/destroy a cell's "- ▽ -" placeholder to match the desired state.
 * @param {object} scene
 * @param {any} cell
 * @param {boolean} wanted
 */
function syncLabel(scene, cell, wanted) {
	if (wanted && !cell.label) {
		const label = new PIXI.Text({
			text: '-    ▽    -',
			style: { fontFamily: 'system-ui', fontSize: Math.max(14, cell.h * 0.16), fill: 0xffffff },
		});
		label.anchor.set(0.5);
		label.x = cell.cellX + cell.w / 2;
		label.y = cell.cellY + cell.h / 2;
		scene.layers.board.addChild(label);
		cell.label = label;
	} else if (!wanted && cell.label) {
		cell.label.destroy();
		cell.label = null;
	}
}

/**
 * FEEDBACK pass: restyle every cell's ring/dim for the current targeting
 * state. Mutates in place — no rebuilds, no flicker.
 * @param {object} scene
 * @returns {void}
 */
export function applyBoardTargeting(scene) {
	if (!scene.boardCells) return;
	for (const row of scene.boardCells) {
		for (const cell of row) {
			styleBoardCell(cell);
		}
	}
}

/**
 * Style one cell for the current targeting state.
 * @param {any} cell
 */
function styleBoardCell(cell) {
	const highlight = highlightFor(cell.spot);

	// v2 HIGHLIGHTS (highlight-fx.js): a crisp outline traces the tile —
	// teal = pickable, green = selected, orange = a legal target. Occupied
	// cells outline their tile art; empty target cells outline their plate.
	applyHighlight(cell.wrap, cell.wrap ? highlight : null);
	applyHighlight(cell.plate, !cell.wrap ? highlight : null);

	/* v1 RING DRAWING — replaced by the outline filters above:
	const radius = Math.min(12, cell.w * 0.06);
	killPulse(cell.ring);
	gsap.killTweensOf(cell.ring);
	cell.ring.clear();
	cell.ring.alpha = 1;
	if (highlight && highlight !== 'pickable') {
		const color = HIGHLIGHT_COLORS[highlight];
		cell.ring
			.roundRect(0, 0, cell.w, cell.h, radius)
			.stroke({ width: Math.max(4, cell.w * 0.025) * (cell.hovered ? 1.8 : 1), color });
		if (!cell.wrap) {
			// Empty cell: a soft fill so the drop target reads on small screens.
			cell.ring.roundRect(0, 0, cell.w, cell.h, radius).fill({ color, alpha: 0.16 });
		}
		if (highlight === 'targeted') {
			// Legal targets: solid ring + a gentle dip every 10s.
			gentlePulse(cell.ring);
		}
	}
	*/

	// Two distinct inert states, never combined:
	//   KNOCKED OUT (health at ZERO — helpless, waiting to be devoured):
	//     the tile SLUMPS — slightly crooked, nudged back toward its owner —
	//     and its ART goes properly DARK. Eggs excluded (they shatter
	//     instead of lingering at zero).
	//   CAN'T ATTACK (`ready` false — summoning sickness, already attacked,
	//     tapped): a LIGHTER dim on the art (+ grayed gui), tile upright.
	//     Highlights override this one so actionable spots stay vivid.
	// Both sides use the same rules, so an upright full-color opponent
	// creature reads as a live threat.
	const zeroed = !!cell.data && !cell.data.trueForm && Number(cell.data.health) === 0;
	const unready = !!cell.data && !cell.data.ready && !zeroed && !highlight;
	const tile = cell.wrap?.children?.[0];
	if (tile) {
		applyCardFilters(
			tile,
			zeroed ? ZERO_ART_FILTER : unready ? UNREADY_ART_FILTER : null,
			unready ? GRAYSCALE_GUI_FILTER : null,
		);
		const lean = ((cell.x + cell.y) % 2 ? -1 : 1) * 0.055; // per-slot, stable
		const back = (cell.y >= 2 ? 1 : -1) * cell.h * 0.06;   // toward the owner
		const posed = zeroed ? 1 : 0;
		if (tile.__posed !== posed) {
			// Ease into/out of the slump once per state change; restyles with
			// the same state leave the tween/pose alone.
			tile.__posed = posed;
			gsap.to(tile, {
				rotation: zeroed ? lean : 0,
				y: cell.h / 2 + (zeroed ? back : 0),
				duration: 0.25,
				ease: 'power2.out',
			});
		}
	}
	if (cell.wrap) cell.wrap.alpha = 1;
	cell.plate.alpha = 1;
	if (cell.label) cell.label.alpha = highlight ? 1 : 0.5;
}

/**
 * Wire tap + press-hold-zoom on a board cell (old PlanZoom/EndZoom behavior:
 * hold 250ms shows the big card; releasing earlier is a click). Handlers read
 * cell.data/cell.spot LIVE, so a reconciled cell never has stale closures.
 * @param {object} scene
 * @param {PIXI.Container} target
 * @param {any} cell
 */
/** Mouse users get pre-click feedback (touch devices skip the listeners). */
const FINE_POINTER = typeof window !== 'undefined' && window.matchMedia?.('(pointer: fine)').matches;

function wireCellInput(scene, target, cell) {
	target.eventMode = 'static';
	target.cursor = 'pointer';
	target.on('pointerdown', () => {
		if (cell.data) scene.beginZoom(cell.data);
	});
	target.on('pointerup', () => {
		if (scene.endZoom()) return; // the zoom consumed this press
		click(cell.spot);
	});
	target.on('pointerupoutside', () => scene.cancelZoom());
	if (FINE_POINTER) {
		// Desktop hover: an actionable cell's ring brightens under the mouse.
		target.on('pointerover', () => {
			cell.hovered = true;
			styleBoardCell(cell);
		});
		target.on('pointerout', () => {
			cell.hovered = false;
			styleBoardCell(cell);
		});
	}
}
